import React, { useEffect, useCallback, FC } from 'react';

import { TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

import { useAlert } from '../../context/AlertProvider';
import styled from '../../styled';
import { MIDDLE_MARGIN_PX, SMALL_MARGIN_PX } from '../../theme';
import { ContactFormItem } from '../../types';
import { sendDataToGTM } from '../../utils/helpers';
import useValidation from '../../utils/useValidation';
import { CustomButton } from '../UI/CustomButton';
import { contactFormSchema } from './validation';

const triggerSubmitEvent = () => {
  if (!document) return;

  let event;
  if (document.createEvent) {
    event = document.createEvent('HTMLEvents');
    event.initEvent('submit', true, true);
    event.eventName = 'submit';
    document.dispatchEvent(event);
  } else {
    // @ts-ignore
    event = document.createEventObject();
    event.eventName = 'submit';
    event.eventType = 'submit';
    // @ts-ignore
    document.fireEvent('on' + event.eventType, event);
  }
};

const initialValues = {
  name: '',
  email: '',
  reason: null,
  message: '',
};

interface ContactFormProps {
  reasonItems: ContactFormItem[];
  userName?: string | null;
  userEmail?: string | null;
  userComFormId?: string | null;
  nameField?: string | null;
  emailField?: string | null;
  contactReasonField?: string | null;
  contactMessageField?: string | null;
}

export const ContactForm: FC<ContactFormProps> = ({
  reasonItems,
  userName,
  userEmail,
  userComFormId,
  nameField,
  emailField,
  contactReasonField,
  contactMessageField,
}) => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const onSubmit = useCallback(
    async (values, { setDataValue }) => {
      if (window.userengage) {
        try {
          const event = values?.reason?.is_sales_request
            ? 'event.form_conversion'
            : 'event.contact_form_submitted';

          const name = values.name.split(' ');
          const firstName = name[0];
          const lastName = name.length > 1 ? name.slice(1).join(' ') : '';

          //used window.userengage instead of UE.pageHit, because it returns promise
          await window.userengage('client.update', {
            //name attribute does not update user name in user.com,just save it as custom attribute
            first_name: firstName,
            last_name: lastName,
            ...(emailField && { [emailField]: values.email }),
            ...(contactReasonField && { [contactReasonField]: values.reason?.type }),
            ...(contactMessageField && {
              [contactMessageField]: {
                message: values.message.trim(),
              },
            }),
          });

          //fill "formEmail" dataLayer field for conversion tracking
          sendDataToGTM({
            formEmail: values.email,
          });

          await window.userengage(event, {
            form_id: userComFormId,
            ...(nameField && { [nameField]: values.name }),
            ...(emailField && { [emailField]: values.email }),
            ...(contactReasonField && { [contactReasonField]: values.reason?.type }),
            ...(contactMessageField && {
              [contactMessageField]: {
                message: values.message.trim(),
              },
            }),
          });

          showAlert({ message: t('alert.messageSendSuccessfully') });
          triggerSubmitEvent();

          setDataValue({
            ...initialValues,
            name: userName || '',
            email: userEmail || '',
          });
        } catch (e) {
          Sentry.captureException(e);
        }
      }
    },
    [
      showAlert,
      userName,
      userEmail,
      userComFormId,
      nameField,
      emailField,
      contactReasonField,
      contactMessageField,
      t,
    ],
  );

  const { values, errors, handleChange, setFieldManually, handleSubmit } = useValidation(
    initialValues,
    contactFormSchema,
    onSubmit,
  );

  useEffect(() => {
    if (userName) {
      setFieldManually('name', userName);
    }
  }, [userName, setFieldManually]);

  useEffect(() => {
    if (userEmail) {
      setFieldManually('email', userEmail);
    }
  }, [userEmail, setFieldManually]);

  return (
    <>
      <StyledForm
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          //prevent submit event for validation
          e.stopPropagation();
          handleSubmit();
        }}
      >
        <ContactFormFieldWrap>
          <ContactFormField
            inputProps={{ 'data-testid': 'nameField' }}
            name="name"
            label={t('input.placeholder.yourName')}
            variant="outlined"
            value={values.name}
            onChange={handleChange}
            error={Boolean(errors?.name)}
            helperText={errors?.name}
          />
        </ContactFormFieldWrap>
        <ContactFormFieldWrap>
          <ContactFormField
            inputProps={{ 'data-testid': 'emailField' }}
            name="email"
            label={t('input.placeholder.yourEmail')}
            variant="outlined"
            value={values.email}
            onChange={handleChange}
            error={Boolean(errors?.email)}
            helperText={errors?.email}
          />
        </ContactFormFieldWrap>
        <ContactFormFieldWrap>
          <Autocomplete
            data-testid="departmentField"
            options={reasonItems}
            getOptionLabel={(option) => option?.question || ''}
            value={values.reason}
            onChange={(event, value) => {
              setFieldManually('reason', value || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name="reason"
                label={t('input.placeholder.howCanWeHelp')}
                variant="outlined"
                error={Boolean(errors?.reason)}
                helperText={errors?.reason}
              />
            )}
          />
        </ContactFormFieldWrap>

        <ContactFormField
          inputProps={{ 'data-testid': 'messageField' }}
          name="message"
          label={t('input.placeholder.message')}
          variant="outlined"
          multiline
          rows={9}
          value={values.message}
          onChange={handleChange}
          error={Boolean(errors?.message)}
          helperText={errors?.message}
        />

        <StyledCustomButton type="submit" data-testid="submit">
          {t('button.submit')}
        </StyledCustomButton>
      </StyledForm>
    </>
  );
};

const StyledForm = styled('form')`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    align-items: flex-start;
  }
`;
const ContactFormFieldWrap = styled('div')`
  margin-bottom: ${SMALL_MARGIN_PX};
  width: 100%;
`;
const ContactFormField = styled(TextField)`
  width: 100%;
`;
const StyledCustomButton = styled(CustomButton)`
  margin-top: ${MIDDLE_MARGIN_PX};
`;
